import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMobile } from '../hooks/useMobile';
import './display-mode.css';

const MOBILE_MODES = ['normal', 'compact'];
const DESKTOP_MODES = [...MOBILE_MODES, 'extra-compact'];
const validMode = value => DESKTOP_MODES.includes(value);
const modeForDevice = (mode, isMobile) => isMobile && mode === 'extra-compact' ? 'compact' : mode;
const storageKey = userId => `trainnova.displayMode.${userId}`;

function readMode(userId) {
  if (!userId) return 'normal';
  try {
    const saved = window.localStorage.getItem(storageKey(userId));
    return validMode(saved) ? saved : 'normal';
  } catch { return 'normal'; }
}

// Device-local, account-scoped preference. It must not enter the theme API or
// follow the selected team. Storage is only written by an explicit user choice.
export function useDisplayMode(userId) {
  const isMobile = useMobile();
  const displayModeOptions = isMobile ? MOBILE_MODES : DESKTOP_MODES;
  const identity = useRef(userId);
  identity.current = userId;
  const cached = useMemo(() => modeForDevice(readMode(userId), isMobile), [userId, isMobile]);
  const [selection, setSelection] = useState(() => ({ userId, mode: cached, saved: true }));
  const current = selection.userId === userId ? selection : { mode: cached, saved: true };

  useLayoutEffect(() => {
    setSelection({ userId, mode: cached, saved: true });
  }, [userId, cached]);

  useLayoutEffect(() => {
    document.documentElement.dataset.displayMode = current.mode;
    document.documentElement.dataset.displayDevice = isMobile ? 'mobile' : 'desktop';
    const frame = window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => window.cancelAnimationFrame(frame);
  }, [current.mode, isMobile]);

  useEffect(() => () => {
    delete document.documentElement.dataset.displayMode;
    delete document.documentElement.dataset.displayDevice;
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    const onStorage = event => {
      if (identity.current !== userId) return;
      try {
        if (event.storageArea !== window.localStorage) return;
      } catch { return; }
      if (event.key !== null && event.key !== storageKey(userId)) return;
      // Removal, clear(), and invalid values all restore Normal.
      const mode = event.key !== null && validMode(event.newValue) ? event.newValue : 'normal';
      setSelection({ userId, mode: modeForDevice(mode, isMobile), saved: true });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId, isMobile]);

  const changeDisplayMode = useCallback(next => {
    // A callback retained by an old account must never change the new account.
    if (!userId || identity.current !== userId || !displayModeOptions.includes(next)) return;
    let saved = true;
    try { window.localStorage.setItem(storageKey(userId), next); }
    catch { saved = false; }
    setSelection({ userId, mode: next, saved });
  }, [userId, displayModeOptions]);

  return { displayMode: current.mode, displayModeSaved: current.saved, displayModeOptions, changeDisplayMode };
}
